import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { Helmet } from "react-helmet";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Enhanced LocalBusiness schema for SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "name": "Altum Contractors",
    "alternateName": "Altum Roofing",
    "image": [
      "https://altumcontractors.com/logo.png",
      "https://lpbjlsiluhyymjpqsaop.supabase.co/storage/v1/object/public/blog-images/Gallery/altum-contractors-service-van-houston-tx.png"
    ],
    "url": "https://altumcontractors.com",
    "telephone": "+1-346-220-0989",
    "email": "Contact@altumcontractors.com",
    "description": "Houston's trusted roofing contractor since 2003. Expert roof repairs, replacements, and maintenance with honest pricing and quality craftsmanship.",
    "foundingDate": "2003",
    "founder": {
      "@type": "Person",
      "name": "Jose D. Campos"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Houston",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 29.7604,
      "longitude": -95.3698
    },
    "areaServed": [
      { "@type": "City", "name": "Houston" },
      { "@type": "City", "name": "The Woodlands" },
      { "@type": "City", "name": "Spring" },
      { "@type": "City", "name": "Katy" },
      { "@type": "City", "name": "Sugar Land" },
      { "@type": "City", "name": "Pearland" },
      { "@type": "City", "name": "Cypress" },
      { "@type": "City", "name": "Kingwood" },
      { "@type": "City", "name": "Clear Lake" }
    ],
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card, Check, Financing Available",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "14:00"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Roofing Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Replacement" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Repairs" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Maintenance" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Storm Damage Repair" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Skylights and Ventilation" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "New Construction Roofing" } }
      ]
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61572956907963",
      "https://www.instagram.com/altumcontractors/",
      "https://www.linkedin.com/company/altum-contractors/"
    ],
    "knowsAbout": [
      "Residential Roofing",
      "Shingle Roofing",
      "Roof Leak Repair",
      "Storm Damage Assessment",
      "Insurance Claims Assistance",
      "Owens Corning Roofing Systems"
    ],
    "slogan": "Craftsmanship Made Simple"
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      <footer ref={footerRef} className="bg-charcoal text-white relative overflow-hidden">
        {/* Gradient fade from page to footer */}
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-background to-transparent -translate-y-full" />
        
        <div className="pt-16 pb-10">
      <div className="container mx-auto px-4">
        {/* Accent Line */}
        <div className="footer-accent-line" />

        <div className="grid md:grid-cols-5 gap-12 mb-12">
          {/* Company Info */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "0ms",
            }}
          >
            <div className="mb-4 inline-block">
              <div className="text-3xl font-heading font-bold tracking-wide" aria-label="Altum Contractors">ALTUM</div>
              <span className="block bg-primary text-white text-[0.5rem] font-bold tracking-[0.2em] px-1 py-0.5 text-center">
                CONTRACTORS
              </span>
            </div>
            <p className="text-white/80 leading-relaxed mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61572956907963"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-primary hover:scale-110 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/altumcontractors/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-primary hover:scale-110 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/altum-contractors/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-primary hover:scale-110 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "100ms",
            }}
          >
            <p className="text-lg font-heading font-semibold mb-4" role="heading" aria-level={2}>{t('footer.quickLinks')}</p>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('nav.services')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("process")}
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('footer.process')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("testimonials")}
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('footer.reviews')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("final-cta")}
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('footer.contact')}
                </button>
              </li>
              <li>
                <a
                  href="/book-inspection"
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('footer.scheduleInspection')}
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('nav.about')}
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  className="footer-link text-white/80 hover:text-white transition-colors"
                >
                  {t('nav.blog')}
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "200ms",
            }}
          >
            <p className="text-lg font-heading font-semibold mb-4" role="heading" aria-level={2}>{t('footer.services')}</p>
            <ul className="space-y-2">
              <li>
                <a href="/services/roof-replacement" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('services.roofReplacement')}
                </a>
              </li>
              <li>
                <a href="/services/roof-repairs" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('services.roofRepairs')}
                </a>
              </li>
              <li>
                <a href="/services/roof-maintenance" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('services.preventativeMaintenance')}
                </a>
              </li>
              <li>
                <a href="/services/skylights-ventilation" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('services.enhancements')}
                </a>
              </li>
              <li>
                <a href="/services/new-construction" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('services.newConstruction')}
                </a>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "250ms",
            }}
          >
            <p className="text-lg font-heading font-semibold mb-4" role="heading" aria-level={2}>{t('footer.serviceAreas')}</p>
            <ul className="space-y-2">
              <li>
                <a href="/houston-roofing" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.greaterHouston')}
                </a>
              </li>
              <li>
                <a href="/roofing-the-woodlands-tx" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.theWoodlands')}
                </a>
              </li>
              <li>
                <a href="/roofing-kingwood-tx" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.kingwood')}
                </a>
              </li>
              <li>
                <a href="/roofing-atascocita-tx" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.atascocita')}
                </a>
              </li>
              <li>
                <a href="/roofing-humble-tx" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.humble')}
                </a>
              </li>
              <li>
                <a href="/roofing-spring-tx" className="footer-link text-white/80 hover:text-white transition-colors">
                  {t('areas.spring')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "300ms",
            }}
          >
            <p className="text-lg font-heading font-semibold mb-4" role="heading" aria-level={2}>{t('footer.contactUs')}</p>
            <div className="space-y-4">
              <a
                href="tel:+13462200989"
                className="flex items-start gap-3 text-white/80 hover:text-white transition-all duration-300 group"
              >
                <Phone className="w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-300 group-hover:scale-110" />
                <span className="footer-link">(346) 220-0989</span>
              </a>
              <a
                href="mailto:Contact@altumcontractors.com"
                className="flex items-start gap-3 text-white/80 hover:text-white transition-all duration-300 group"
              >
                <Mail className="w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-300 group-hover:scale-110" />
                <span className="footer-link">Contact@altumcontractors.com</span>
              </a>
              <div className="flex items-start gap-3 text-white/80">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
                <span>
                  Houston, TX
                  <br />
                  {t('footer.address')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8 text-center text-white/60">
          <p>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
